import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Alert, Badge, Button, Dialog, EmptyState, Inline, Link, Stack, t, Text, TextField, toast } from '@kanbios/ui';
import type { fleetState, maintenance } from '@kanbios/api-client';
import { api, ApiFailure, unwrap } from '../lib/api';
import { pendingLabel } from './PendingButton';
import { openPullRequestOf, PullRequestConflict } from './PullRequestConflict';
import { type DotSpec, HealthLegend, healthColumn } from './StateColumn';
import { MoreCount } from './MoreCount';
import { ListTable, type ListResult } from './ListTable';
import { runningList } from './RunningPanel';

/** D120: a deployment's external or shared bricks, said once across its servers by the server. */
const deploymentBricks = <T,>(deployment: string, kind: 'external' | 'shared') =>
  async (query: Record<string, unknown>) => unwrap(await api.fleetState.GET('/deployments/{deployment}/bricks/{kind}', { params: { path: { deployment, kind }, query: query as never } })) as unknown as ListResult<T>;
import { changeRef } from './catalogue/PullRequestNotice';

type Running = fleetState.components['schemas']['Running'];
type External = Running['external'][number];
type Shared = Running['shared'][number];
type Ignored = Running['ignored'][number];
type Rules = maintenance.components['schemas']['DeploymentRules'];
type ContainerRule = maintenance.components['schemas']['ContainerRule'];
type Field = keyof Rules;

const FIELDS: Field[] = ['containers', 'units', 'types', 'external', 'alsoUses'];
const EMPTY: Required<Rules> = { containers: '', units: '', types: '', external: '', alsoUses: '' };

/**
 * D48 « Modifier les règles », from a deployment's detail on its server: which
 * containers, services and cron jobs are its bricks, their forced types, its
 * external bricks and the bricks it shares, in the catalogue's own syntax. The
 * backend checks them with the rules of « Déclarer une application »; one pull
 * request to the catalogue.
 */
export function DeploymentRulesDialog({ server, deployment, title, onClose, onOpened }: {
  server: string; deployment: string; title: string; onClose: () => void; onOpened: () => void;
}) {
  const [draft, setDraft] = useState<Required<Rules> | null>(null);
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const path = { server, deployment };
  const current = useQuery({
    queryKey: ['deployment-rules', server, deployment],
    queryFn: async () => unwrap(await api.maintenance.GET('/servers/{server}/deployments/{deployment}/rules', { params: { path } })),
    retry: false,
  });
  useEffect(() => {
    if (current.data && !draft) setDraft({ ...EMPTY, ...current.data.rules });
  }, [current.data, draft]);
  const preview = useQuery({
    queryKey: ['deployment-rules-preview', server, deployment, draft],
    queryFn: async () => unwrap(await api.maintenance.POST('/servers/{server}/deployments/{deployment}/rules/preview', { params: { path }, body: draft! })),
    placeholderData: keepPreviousData,
    enabled: Boolean(draft),
  });
  const errorOf = (field: Field) => preview.data?.errors.filter(e => e.field === field).map(e => e.message).join(' ') || undefined;
  const ready = Boolean(draft && preview.data && !preview.isFetching && !preview.data.errors.length && preview.data.changed);

  const submit = async () => {
    if (!draft) return;
    setSending(true);
    setFailure(null);
    try {
      const { pullRequest } = unwrap(await api.maintenance.PUT('/servers/{server}/deployments/{deployment}/rules', { params: { path }, body: draft }));
      toast({ tone: 'pos', title: t('bricks.rules.opened', { ref: changeRef(pullRequest) }) });
      onOpened();
    } catch (e) {
      if (e instanceof ApiFailure) setFailure(e);
      else throw e;
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open
      size="lg"
      onClose={onClose}
      title={t('bricks.rules.title', { deployment: title })}
      description={t('bricks.rules.lead', { server })}
      confirmLabel={pendingLabel(t('bricks.rules.confirm'), sending)}
      confirmDisabled={!ready || sending}
      onConfirm={() => void submit()}
    >
      <Stack gap={16}>
        {current.data?.pullRequest && (
          <Alert tone="info" title={t('bricks.rules.pending')}>
            <Link href={current.data.pullRequest.url} external>{t('pullRequest.link', { ref: changeRef(current.data.pullRequest) })}</Link>
          </Alert>
        )}
        {current.error && <Alert tone="neg" title={current.error instanceof ApiFailure ? current.error.body.message : t('bricks.rules.unreadable')} />}
        {draft && FIELDS.map(field => (
          <TextField
            key={field}
            id={`rules-${field}`}
            mono
            label={t(`bricks.rules.fields.${field}`)}
            hint={t(`bricks.rules.hints.${field}`)}
            value={draft[field]}
            onChange={(e: { target: { value: string } }) => setDraft(d => ({ ...d!, [field]: e.target.value }))}
            error={errorOf(field)}
          />
        ))}
        {draft && preview.data && !preview.data.errors.length && !preview.data.changed && <Text variant="body-sm" tone="ink-2">{t('bricks.rules.unchanged')}</Text>}
        {failure && openPullRequestOf(failure) ? (
          <PullRequestConflict failure={failure} onClosed={() => setFailure(null)} />
        ) : failure && (
          <Alert tone="neg" title={failure.body.message}>{failure.body.hint}</Alert>
        )}
      </Stack>
    </Dialog>
  );
}

/**
 * D48 « Ignorer » a detected group (its rules join the server's
 * `kanbios/ignored-bricks`), or « Ne plus ignorer » one entry: a pull request to the catalogue.
 */
export function IgnoreDialog({ server, name, change, onClose, onOpened }: {
  server: string;
  /** The group or the rule, as the person reads it. */
  name: string;
  change: { add: { containers: ContainerRule[]; units: { kind: 'systemd' | 'cron'; prefix: string }[] } } | { remove: string[] };
  onClose: () => void;
  onOpened: () => void;
}) {
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const adding = 'add' in change;
  const submit = async () => {
    setSending(true);
    setFailure(null);
    try {
      const { pullRequest } = unwrap(await api.maintenance.PUT('/servers/{server}/ignored', { params: { path: { server } }, body: change }));
      toast({ tone: 'pos', title: t(adding ? 'bricks.ignore.opened' : 'bricks.unignore.opened', { ref: changeRef(pullRequest) }) });
      onOpened();
    } catch (e) {
      if (e instanceof ApiFailure) setFailure(e);
      else throw e;
    } finally {
      setSending(false);
    }
  };
  return (
    <Dialog
      open
      size="md"
      onClose={onClose}
      title={t(adding ? 'bricks.ignore.title' : 'bricks.unignore.title', { name })}
      description={t(adding ? 'bricks.ignore.lead' : 'bricks.unignore.lead', { server })}
      confirmLabel={pendingLabel(t('bricks.ignore.confirm'), sending)}
      confirmDisabled={sending}
      onConfirm={() => void submit()}
    >
      {failure && openPullRequestOf(failure) ? (
        <PullRequestConflict failure={failure} onClosed={() => setFailure(null)} />
      ) : failure ? (
        <Alert tone="neg" title={failure.body.message}>{failure.body.hint}</Alert>
      ) : null}
    </Dialog>
  );
}

/** D53: an external brick's check as a dot (fault: a halo beside the green of OK; not checked yet: a ring). */
const EXTERNAL_ORDER = ['fault', 'warn', 'pending', 'ok'] as const;
const EXTERNAL_TONES = { ok: 'pos', warn: 'warn', fault: 'neg', pending: 'neutral' } as const;
const externalSpec = (b: External): DotSpec => ({ tone: EXTERNAL_TONES[b.state], label: t(`bricks.external.states.${b.state}`), description: b.output ?? undefined });
const EXTERNAL_LEGEND = (): DotSpec[] => EXTERNAL_ORDER.map(state => ({ tone: EXTERNAL_TONES[state], label: t(`bricks.external.states.${state}`), description: t(`bricks.external.statesHelp.${state}`) }));

/** D48: a deployment's external bricks (managed database, storage, third-party API), each with its HTTP or TCP check. */
export function ExternalBricksTable({ label, deployment }: { label: string; deployment: string }) {
  return (
    <ListTable<External>
      label={label}
      queryKey={['deployment-bricks', deployment, 'external']}
      fetch={deploymentBricks<External>(deployment, 'external')}
      rowKey={(b: External) => `${b.deployment}/${b.name}`}
      defaultSort={{ key: 'state', dir: 'asc' }}
      search={t('bricks.external.search')}
      facets={[
        { key: 'state', label: t('bricks.external.state'), valueLabel: v => t(`bricks.external.states.${v}`) },
        { key: 'type', label: t('bricks.external.type'), valueLabel: v => t(`bricks.type.${v}`) },
      ]}
      refetchInterval={60_000}
      columns={[
        healthColumn<External>({ legendLabel: t('bricks.external.legend'), legendTitle: t('bricks.external.legendTitle'), legend: <HealthLegend items={EXTERNAL_LEGEND()} />, spec: externalSpec }),
        { key: 'name', label: t('bricks.external.name'), mono: true, strong: true, pinned: true, grow: true, width: 180 },
        { key: 'type', label: t('bricks.external.type'), width: 120, accessor: (b: External) => t(`bricks.type.${b.type}`), render: (b: External) => <Badge tone="neutral" size="sm">{t(`bricks.type.${b.type}`)}</Badge> },
        { key: 'target', label: t('bricks.external.target'), mono: true, width: 260, sortable: false },
        { key: 'output', label: t('bricks.external.output'), width: 260, sortable: false, accessor: (b: External) => b.output ?? '—' },
      ]}
      empty={<EmptyState framed={false} icon="cloud" title={t('bricks.external.empty')} />}
    />
  );
}

/** D48: the bricks a deployment shares (its own, used by others; others', used by it), with the owner's fault. */
export function SharedBricksTable({ label, deployment, environmentLabel }: { label: string; deployment: string; environmentLabel: (value: string) => string }) {
  const name = (d: Shared['owner']) => `${d.application} · ${environmentLabel(d.environment)}`;
  return (
    <ListTable<Shared>
      label={label}
      queryKey={['deployment-bricks', deployment, 'shared']}
      fetch={deploymentBricks<Shared>(deployment, 'shared')}
      rowKey={(l: Shared) => `${l.owner.deployment}/${l.brick}>${l.user.deployment}`}
      defaultSort={{ key: 'brick', dir: 'asc' }}
      search={t('bricks.shared.search')}
      facets={[{ key: 'fault', label: t('bricks.shared.state'), valueLabel: v => (v === 'none' ? t('applications.state.ok') : t(`bricks.fault.${v}`)) }]}
      refetchInterval={60_000}
      columns={[
        { key: 'brick', label: t('bricks.shared.brick'), mono: true, strong: true, pinned: true, grow: true, width: 160 },
        { key: 'owner', label: t('bricks.shared.owner'), width: 240, accessor: (l: Shared) => name(l.owner) },
        { key: 'user', label: t('bricks.shared.user'), width: 240, accessor: (l: Shared) => name(l.user) },
        { key: 'fault', label: t('bricks.shared.state'), width: 200, sortable: false, accessor: (l: Shared) => l.fault ?? '', render: (l: Shared) => (l.fault
          ? <Badge tone="neg" size="sm">{t(`bricks.fault.${l.fault}`)}</Badge>
          : <Text variant="body-sm" tone="ink-2" truncate>{t('applications.state.ok')}</Text>) },
      ]}
      empty={<EmptyState framed={false} icon="share" title={t('bricks.shared.empty')} />}
    />
  );
}

/** D48: the groups ignored on the server, what each one takes, and « Ne plus ignorer ». */
export function IgnoredTable({ server, onUnignore }: { server: string; onUnignore?: (rule: Ignored) => void }) {
  return (
    <ListTable<Ignored>
      label={t('bricks.ignored.table')}
      queryKey={['server-running-list', server, 'ignored']}
      fetch={runningList<Ignored>(server, 'ignored')}
      rowKey="rule"
      defaultSort={{ key: 'rule', dir: 'asc' }}
      search={t('bricks.ignored.search')}
      facets={[{ key: 'kind', label: t('bricks.ignored.kind'), valueLabel: v => t(`bricks.ignored.kinds.${v}`) }]}
      refetchInterval={60_000}
      columns={[
        { key: 'rule', label: t('bricks.ignored.rule'), mono: true, strong: true, pinned: true, grow: true, width: 280 },
        { key: 'matches', label: t('bricks.ignored.matches'), width: 280, sortable: false, accessor: (r: Ignored) => r.matches.join(' '), render: (r: Ignored) => (r.matches.length
          ? (
            <Inline gap={8} align="center" wrap={false}>
              <Text variant="code" truncate>{r.matches[0]}</Text>
              <MoreCount n={r.matches.length - 1} title={r.matches.slice(1).join(' · ')} />
            </Inline>
          )
          : <Text variant="body-sm" tone="ink-2" truncate>{t('bricks.ignored.none')}</Text>) },
        ...(onUnignore ? [{
          key: 'action', label: '', width: 170, sortable: false, pinned: 'right' as const,
          render: (r: Ignored) => <Button variant="tonal" size="sm" onClick={() => onUnignore(r)} aria-label={t('bricks.ignored.unignoreOf', { rule: r.rule })}>{t('bricks.ignored.unignore')}</Button>,
        }] : []),
      ]}
      empty={<EmptyState framed={false} icon="visibility_off" title={t('bricks.ignored.empty')} />}
    />
  );
}
